import type { Session, SupabaseClient } from '@supabase/supabase-js';

type EmailAuth = Pick<SupabaseClient['auth'], 'signInWithOtp' | 'verifyOtp'>;
type SessionAuth = Pick<SupabaseClient['auth'], 'getSession' | 'getUser'>;

export function requestEmailCode(auth: EmailAuth, email: string) {
  return auth.signInWithOtp({ email: email.trim() });
}

export function verifyEmailCode(auth: EmailAuth, email: string, code: string) {
  return auth.verifyOtp({
    email: email.trim(),
    token: code.trim(),
    type: 'email',
  });
}

export function emailAuthError(error: { code?: string; message?: string }) {
  if (error.code === 'otp_expired' || /expired/i.test(error.message ?? ''))
    return 'O código expirou ou já foi usado. Solicite um novo código.';
  if (error.code === 'otp_disabled')
    return 'O acesso por e-mail está indisponível. Contate o administrador.';
  if (
    error.code === 'over_email_send_rate_limit' ||
    error.code === 'over_request_rate_limit'
  )
    return 'Aguarde um pouco antes de solicitar outro código.';
  return 'Não foi possível confirmar o código. Confira os dígitos e tente novamente.';
}

export function legacyLinkError(hash: string) {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (!params.has('error')) return null;
  return params.get('error_code') === 'otp_expired'
    ? 'Esse link de acesso expirou ou já foi usado. Solicite um código novo abaixo.'
    : 'Não foi possível usar esse link. Solicite um código novo abaixo.';
}

export async function getValidatedSession(
  auth: SessionAuth,
): Promise<Session | null> {
  const initial = await auth.getSession();
  if (initial.error || !initial.data.session) return null;
  const verified = await auth.getUser();
  if (verified.error || !verified.data.user) return null;
  const latest = await auth.getSession();
  const session = latest.data.session;
  if (
    latest.error ||
    !session ||
    session.user.id !== verified.data.user.id ||
    (session.expires_at !== undefined &&
      session.expires_at <= Date.now() / 1000)
  )
    return null;
  return session;
}
